/**
 * The note lane column view (W2-03 note-lane; contract `TimelineColumnView` in render/types.ts, ARCHITECTURE.md §7.7,
 * DESIGN.md §4.6).
 *
 * One view per lane instance (the active line's lane and each pinned extra lane). It draws, hit-tests and answers
 * marquees; it never touches the document or the editor store (render code may not import state/ or app/). Everything
 * it shows comes from an injected `LaneViewModel`, and every pointer gesture is handed to injected `LaneHandlers`
 * enriched with the lane's own geometry (RPE X of the pointer, the note and hold part under it, the transform box
 * handle under it). The note-lane feature (src/features/note-lane) builds both.
 *
 *   layers   static: field, waveform backdrop, beat grid, X grid (laneDraw.drawLaneStatic);
 *            content: ghost notes of other lines, the line's notes;
 *            fixed (pinned to the visible area, `drawFixed`): the X ruler band and the empty-state message. Static
 *            and content are placed by beats only, so the host's scroll cache may draw them for a taller strip;
 *            overlay: hover / preview-link outline, lane point, placement / paste / move ghosts, transform box, eraser
 *            trail and the +X direction arrow.
 *   hits     analytic (laneHit.ts): binary search by beat, then X; `itemsInRect` for the host's marquee (also with
 *            unbounded X sides for time-range selections).
 *   X axis   `zoomX` (Ctrl+Shift+wheel) and `panX` (horizontal wheel, middle drag) change the lane X range through the
 *            model, which mirrors it into the editor store (TimelineViewState.laneCenterX / laneHalfRange).
 *   wheel    `onWheel` offers the wheel to the handlers first (RPE Ctrl+wheel width fine-tune); unconsumed wheels
 *            scroll / zoom as the host does for every column.
 */

import type { LineId, NoteId } from '../../core/model/types';
import type { DirtyScopes, Op } from '../../core/ops/types';
import type {
  TimeAxis,
  TimelineColumnView,
  TimelineHit,
  TimelineLayer,
  TimelinePointerEvent,
  TimelineRectItems,
} from '../types';
import { noteChangeWindow, windowsOverlap, type BeatWindow, type DocScopeView } from './docChange';
import { drawPasteGhost, type PasteGhost } from './ghostPaste';
import {
  BOX_HANDLE_PX,
  drawEraserTrail,
  drawGhostNotes,
  drawHoverOutline,
  drawLaneMessage,
  drawLaneNotes,
  drawLanePoint,
  drawLaneStatic,
  drawMoveGhost,
  drawPlacementGhost,
  drawPlusXArrow,
  drawRuler,
  drawTransformBox,
  FALLBACK_LANE_THEME,
  readLaneTheme,
  RULER_PX,
  timelineThemeOf,
  type LaneNotesStats,
  type LaneTheme,
  type NoteDrawState,
  type PlacementGhost,
  type TransformBoxPx,
  type TransformHandle,
} from './laneDraw';
import {
  EMPTY_NOTE_INDEX,
  FIELD_HALF_PX,
  hitNote,
  laneXMap,
  notesInRect,
  panXRange,
  zoomXRange,
  type LaneNoteHit,
  type LaneNoteIndex,
  type LaneXMap,
} from './laneHit';
import { isStripAxis } from './axis';
import type { TimelineTheme } from './theme';

/** Column def id and placement (render/types.ts TimelineColumnDef.order: gutter 0, waveform 10, lanes 20). */
export const LANE_DEF_ID = 'lane';
export const LANE_ORDER = 20;
export const LANE_WIDTH = { min: 180, default: 440, max: 4000 } as const;
/** Instance id of the active line's lane (stable across line switches, so its width is kept). */
export const ACTIVE_LANE_ID = 'lane:active';

/** Instance id of a pinned extra lane. */
export function pinnedLaneId(line: LineId): string {
  return `lane:${String(line)}`;
}

/** Line of a pinned lane instance id, or null for the active lane / other ids. */
export function lineOfLaneId(id: string): LineId | null {
  const m = /^lane:(\d+)$/.exec(id);
  return m === null ? null : (Number(m[1]) as LineId);
}

/** Lane X zoom limits (visible half-range in RPE px), as the editor store validates them. */
export const LANE_HALF_MIN = 20;
export const LANE_HALF_MAX = 200000;

/** Transform box in beats (f64) and RPE X; the view converts it to px. */
export interface TransformBoxModel {
  readonly b0: number;
  readonly b1: number;
  readonly x0: number;
  readonly x1: number;
  readonly active: TransformHandle | null;
}

/** What the overlay shows now. */
export interface LaneOverlayModel {
  readonly placement: PlacementGhost | null;
  readonly paste: { readonly ghost: PasteGhost; readonly label: string | null } | null;
  /** Ghost transform of a large drag: these notes are drawn shifted and skipped on the content layer. */
  readonly move: { readonly ids: ReadonlySet<NoteId>; readonly dBeat: number; readonly dx: number } | null;
  readonly box: TransformBoxModel | null;
  readonly eraser: readonly (readonly [number, number])[] | null;
  /** Lane point of the preview link (mouse over empty lane): beat and RPE X. */
  readonly lanePoint: { readonly beat: number; readonly x: number } | null;
  /** Note outlined as hovered (own hover or the preview's). */
  readonly hover: NoteId | null;
}

export const EMPTY_OVERLAY: LaneOverlayModel = Object.freeze({
  placement: null,
  paste: null,
  move: null,
  box: null,
  eraser: null,
  lanePoint: null,
  hover: null,
});

/** Data the lane draws. Every method is cheap; the feature caches indexes per document version. */
export interface LaneViewModel {
  /** Line shown, or null (no active line: the lane shows its empty state). */
  line(): LineId | null;
  notes(): LaneNoteIndex;
  /** Notes of other lines drawn as ghosts (also show). */
  ghosts(): readonly LaneNoteIndex[];
  noteState(i: number, id: NoteId): NoteDrawState;
  /** Notes hits and marquees may take (sides filter); all when absent. */
  readonly include?: (i: number) => boolean;
  xView(): { readonly center: number; readonly half: number };
  setXView(v: { readonly center: number; readonly half: number }): void;
  grid(): { readonly snapDiv: number; readonly xGrid: number; readonly xSnap: boolean; readonly waveform: boolean };
  /** Locked lines are drawn dimmed (editing is refused by the handlers). */
  locked(): boolean;
  /** Translated empty-state lines (title first), or null. */
  message(): readonly string[] | null;
  overlay(axis: TimeAxis, xmap: LaneXMap): LaneOverlayModel;
  /** On-screen angle of +X at the playhead (radians, CCW from screen right), or null. */
  plusXAngle(): number | null;
  /** Cursor over empty lane space (depends on the tool). */
  emptyCursor(): string;
  /** Hit tolerance for touch / pen pointers (CSS px). */
  slop(): number;
}

/** A pointer event with the lane's geometry. */
export interface LanePointer extends TimelinePointerEvent {
  /** RPE X under the pointer. */
  readonly xRpe: number;
  readonly hit: LaneNoteHit | null;
  readonly handle: TransformHandle | null;
}

/** Gesture callbacks (the feature's laneGestures.ts). */
export interface LaneHandlers {
  down(view: LaneView, e: LanePointer): void;
  /** Pointer moves while pressed (also outside the lane). */
  drag(view: LaneView, e: LanePointer): void;
  up(view: LaneView, e: LanePointer): void;
  cancel(view: LaneView): void;
  /** Pointer moves without a press; null when the pointer left the lane. */
  hover(view: LaneView, e: LanePointer | null): void;
  doubleClick(view: LaneView, e: LanePointer): void;
  contextMenu(view: LaneView, e: LanePointer): void;
  /** Wheel over the lane; true when consumed (otherwise the host scrolls / zooms as usual). */
  wheel?(view: LaneView, e: WheelEvent, axis: TimeAxis): boolean;
}

export interface LaneViewDeps {
  readonly instanceId: string;
  readonly model: LaneViewModel;
  readonly handlers: LaneHandlers;
  /** Called when the view is disposed (the feature drops its registration). */
  readonly onDispose?: (view: LaneView) => void;
}

export interface LaneView extends TimelineColumnView, DocScopeView {
  /** Opts into the host's scroll cache (TimelineHost `ScrollCacheView`). */
  readonly scrollCache: true;
  drawFixed(ctx: CanvasRenderingContext2D, axis: TimeAxis, widthPx: number): void;
  readonly instanceId: string;
  readonly model: LaneViewModel;
  /** Current X mapping for the laid-out width. */
  xmap(): LaneXMap;
  readonly width: number;
  /** The live axis of the last overlay draw / hit-test / pointer event (null before the first). */
  readonly axis: TimeAxis | null;
  /** Note under a lane point (sides filter applied), or null. */
  noteAt(x: number, y: number, axis: TimeAxis): LaneNoteHit | null;
  /** Transform box handle under a point, or null. */
  handleAt(x: number, y: number, axis: TimeAxis): TransformHandle | null;
  /** Transform box in lane px for the current axis, or null. */
  boxPx(axis: TimeAxis): TransformBoxPx | null;
  /** Stats of the last content draw (tests, diagnostics). */
  readonly lastStats: LaneNotesStats | null;
  readonly disposed: boolean;
}

/** Transform box in px for a box model (beats up, X right). */
export function boxToPx(box: TransformBoxModel, axis: TimeAxis, xmap: LaneXMap): TransformBoxPx {
  const yA = axis.yAtBeat(box.b0);
  const yB = axis.yAtBeat(box.b1);
  const xA = xmap.toPx(box.x0);
  const xB = xmap.toPx(box.x1);
  const pad = 8;
  return {
    left: Math.min(xA, xB) - pad,
    right: Math.max(xA, xB) + pad,
    top: Math.min(yA, yB) - pad,
    bottom: Math.max(yA, yB) + pad,
  };
}

/** Which handle (if any) of a box in px a point grabs; the body is everything inside the box. */
export function boxHandleAt(box: TransformBoxPx, x: number, y: number): TransformHandle | null {
  const t = BOX_HANDLE_PX;
  const midX = (box.left + box.right) / 2;
  const midY = (box.top + box.bottom) / 2;
  const inX = x >= box.left - t && x <= box.right + t;
  const inY = y >= box.top - t && y <= box.bottom + t;
  if (!inX || !inY) return null;
  if (Math.abs(y - box.top) <= t && Math.abs(x - midX) <= 2 * t + 2) return 'top';
  if (Math.abs(y - box.bottom) <= t && Math.abs(x - midX) <= 2 * t + 2) return 'bottom';
  if (Math.abs(x - box.left) <= t && Math.abs(y - midY) <= 2 * t + 2) return 'left';
  if (Math.abs(x - box.right) <= t && Math.abs(y - midY) <= 2 * t + 2) return 'right';
  return null;
}

const HANDLE_CURSOR: Readonly<Record<TransformHandle, string>> = {
  body: 'move',
  top: 'ns-resize',
  bottom: 'ns-resize',
  left: 'ew-resize',
  right: 'ew-resize',
};

export function createLaneView(deps: LaneViewDeps): LaneView {
  const { model, handlers } = deps;
  let width: number = LANE_WIDTH.default;
  let lastAxis: TimeAxis | null = null;
  let pressed = false;
  let disposed = false;
  let laneTheme: LaneTheme = FALLBACK_LANE_THEME;
  let themeKey: TimelineTheme | null = null;
  let stats: LaneNotesStats | null = null;

  const xmap = (): LaneXMap => {
    const v = model.xView();
    return laneXMap(v.center, v.half, width);
  };

  function themeFor(ctx: CanvasRenderingContext2D, axis: TimeAxis): { theme: TimelineTheme; lane: LaneTheme } {
    const theme = timelineThemeOf(axis);
    if (theme !== themeKey) {
      themeKey = theme;
      const el = (ctx as Partial<CanvasRenderingContext2D>).canvas;
      laneTheme = readLaneTheme(el instanceof Element ? el : null);
    }
    return { theme, lane: laneTheme };
  }

  function noteAt(x: number, y: number, axis: TimeAxis): LaneNoteHit | null {
    const opts = model.include === undefined ? { slop: model.slop() } : { slop: model.slop(), include: model.include };
    return hitNote(model.notes(), axis, xmap(), x, y, opts);
  }

  /** The beat window of one note (own or ghost): what `noteChangeWindow` needs for patches without t/end. */
  function resolveNote(id: NoteId): BeatWindow | null {
    for (const index of [model.notes(), ...model.ghosts()]) {
      const i = index.indexOf(id);
      if (i >= 0) {
        const t = index.t[i] ?? NaN;
        const e = index.end[i] ?? NaN;
        if (Number.isFinite(t) && Number.isFinite(e)) return { lo: Math.min(t, e), hi: Math.max(t, e) };
      }
    }
    return null;
  }

  function boxPx(axis: TimeAxis): TransformBoxPx | null {
    const box = model.overlay(axis, xmap()).box;
    return box === null ? null : boxToPx(box, axis, xmap());
  }

  function handleAt(x: number, y: number, axis: TimeAxis): TransformHandle | null {
    const b = boxPx(axis);
    if (b === null) return null;
    const h = boxHandleAt(b, x, y);
    if (h !== null) return h;
    return x >= b.left && x <= b.right && y >= b.top && y <= b.bottom ? 'body' : null;
  }

  function pointer(e: TimelinePointerEvent, axis: TimeAxis): LanePointer {
    const handle = handleAt(e.x, e.y, axis);
    const hit = handle !== null && handle !== 'body' ? null : noteAt(e.x, e.y, axis);
    return { ...e, xRpe: xmap().toX(e.x), hit, handle };
  }

  const view: LaneView = {
    instanceId: deps.instanceId,
    model,
    xmap,
    get width() {
      return width;
    },
    get axis() {
      return lastAxis;
    },
    get lastStats() {
      return stats;
    },
    get disposed() {
      return disposed;
    },
    noteAt,
    handleAt,
    boxPx,

    layout(widthPx) {
      width = Math.max(1, widthPx);
    },

    scrollCache: true,

    contentAffected(dirty: DirtyScopes, ops: readonly Op[] | undefined, range: readonly [number, number]): boolean {
      // Line-tied drawing (the lock dimming) and, conservatively, this line's own track edits redraw always.
      const line = model.line();
      if (line !== null && (dirty.lineMotion.has(line) || dirty.lineProps.has(line))) return true;
      // Notes (UX-029): the lane draws its own notes, the ghost notes of other lines and the global
      // multi-highlight rings — a simultaneous note on ANY line lights up here — and all of them only at the
      // changed notes' rows, so a change whose notes are outside the drawn beat range cannot change a pixel.
      if (dirty.lineNotes.size === 0) return false;
      const w = noteChangeWindow(ops, resolveNote);
      return w === null || windowsOverlap(w, { lo: range[0], hi: range[1] });
    },

    drawFixed(ctx, axis, widthPx) {
      lastAxis = axis;
      width = Math.max(1, widthPx);
      const { theme, lane } = themeFor(ctx, axis);
      const g = model.grid();
      drawRuler(ctx, width, {
        theme,
        lane,
        xmap: xmap(),
        snapDiv: g.snapDiv,
        xGrid: g.xGrid,
        xSnap: g.xSnap,
        waveform: g.waveform,
        edgeLabels: true,
      });
      const msg = model.message();
      if (msg !== null) drawLaneMessage(ctx, msg, width, axis.heightPx, theme);
    },

    draw(layer: TimelineLayer, ctx, axis, widthPx) {
      // Static and content may be drawn for a strip taller than the view (scroll cache): that axis is never the one
      // pointer events use.
      if (!isStripAxis(axis)) lastAxis = axis;
      width = Math.max(1, widthPx);
      const { theme, lane } = themeFor(ctx, axis);
      const xm = xmap();
      if (layer === 'static') {
        const g = model.grid();
        drawLaneStatic(ctx, axis, width, {
          theme,
          lane,
          xmap: xm,
          snapDiv: g.snapDiv,
          xGrid: g.xGrid,
          xSnap: g.xSnap,
          waveform: g.waveform,
          edgeLabels: true,
          ruler: false,
        });
        return;
      }
      if (layer === 'content') {
        for (const ghost of model.ghosts()) drawGhostNotes(ctx, axis, ghost, width, xm, lane);
        const index = model.notes();
        const ov = model.overlay(axis, xm);
        const moving = ov.move;
        const skip =
          moving === null || moving.ids.size === 0
            ? undefined
            : (i: number): boolean => moving.ids.has((index.ids[i] ?? -1) as NoteId);
        stats = drawLaneNotes(ctx, axis, index, width, {
          theme,
          lane,
          xmap: xm,
          state: (i) => model.noteState(i, (index.ids[i] ?? -1) as NoteId),
          alpha: model.locked() ? 0.55 : 1,
          ...(skip === undefined ? {} : { skip }),
        });
        return;
      }
      // overlay
      const index = model.notes();
      const ov = model.overlay(axis, xm);
      if (ov.lanePoint !== null && ov.placement === null && ov.paste === null) {
        drawLanePoint(ctx, axis, xm, ov.lanePoint.beat, ov.lanePoint.x, lane);
      }
      if (ov.hover !== null) drawHoverOutline(ctx, axis, index, index.indexOf(ov.hover), xm, lane);
      if (ov.move !== null && ov.move.ids.size > 0) {
        const indices: number[] = [];
        for (const id of ov.move.ids) {
          const i = index.indexOf(id);
          if (i >= 0) indices.push(i);
        }
        drawMoveGhost(ctx, axis, index, indices, ov.move.dBeat, ov.move.dx, width, xm, lane);
      }
      if (ov.box !== null) drawTransformBox(ctx, boxToPx(ov.box, axis, xm), ov.box.active, lane);
      if (ov.placement !== null) drawPlacementGhost(ctx, axis, xm, ov.placement, lane, theme);
      if (ov.paste !== null) drawPasteGhost(ctx, axis, xm, width, ov.paste.ghost, ov.paste.label, lane, theme);
      if (ov.eraser !== null) drawEraserTrail(ctx, ov.eraser, lane);
      const angle = model.plusXAngle();
      if (angle !== null && width > 60) drawPlusXArrow(ctx, width - 12, RULER_PX / 2, angle, theme.text);
    },

    hitTest(x, y, axis): TimelineHit {
      lastAxis = axis;
      const handle = handleAt(x, y, axis);
      if (handle !== null && handle !== 'body') return { kind: 'empty', cursor: HANDLE_CURSOR[handle] };
      const hit = noteAt(x, y, axis);
      const line = model.line();
      if (hit !== null) {
        const kind = hit.part === 'head' ? 'noteHead' : hit.part === 'tail' ? 'noteTail' : 'note';
        const cursor = hit.part === 'body' ? (handle === 'body' ? 'move' : 'pointer') : 'ns-resize';
        return line === null ? { kind, note: hit.id, cursor } : { kind, note: hit.id, line, cursor };
      }
      if (handle === 'body') return { kind: 'empty', cursor: 'move' };
      return { kind: 'empty', cursor: model.emptyCursor() };
    },

    itemsInRect(beat0, beat1, x0, x1): TimelineRectItems {
      if (model.line() === null) return {};
      const index = model.notes();
      return { notes: notesInRect(index, xmap(), beat0, beat1, x0, x1, model.include) };
    },

    onPointerDown(e, _hit) {
      const axis = lastAxis;
      if (axis === null) return;
      pressed = true;
      handlers.down(view, pointer(e, axis));
    },

    onPointerMove(e) {
      const axis = lastAxis;
      if (axis === null) return;
      if (pressed) handlers.drag(view, pointer(e, axis));
      else handlers.hover(view, pointer(e, axis));
    },

    onPointerUp(e) {
      const axis = lastAxis;
      const was = pressed;
      pressed = false;
      if (axis === null || !was) return;
      handlers.up(view, pointer(e, axis));
    },

    onPointerCancel() {
      if (!pressed) return;
      pressed = false;
      handlers.cancel(view);
    },

    onPointerLeave() {
      if (!pressed) handlers.hover(view, null);
    },

    onDoubleClick(e) {
      const axis = lastAxis;
      if (axis !== null) handlers.doubleClick(view, pointer(e, axis));
    },

    onContextMenu(e) {
      const axis = lastAxis;
      if (axis !== null) handlers.contextMenu(view, pointer(e, axis));
    },

    onWheel(e, axis) {
      lastAxis = axis;
      return handlers.wheel?.(view, e, axis) ?? false;
    },

    zoomX(factor, x) {
      const next = zoomXRange(xmap(), factor, x);
      model.setXView(clampXView(next));
    },

    panX(dxPx) {
      model.setXView(clampXView(panXRange(xmap(), dxPx)));
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      deps.onDispose?.(view);
    },
  };

  return view;
}

/** Keeps a lane X view inside the zoom limits and the reachable range. */
export function clampXView(v: { readonly center: number; readonly half: number }): { center: number; half: number } {
  const half = Math.min(LANE_HALF_MAX, Math.max(LANE_HALF_MIN, Number.isFinite(v.half) ? v.half : FIELD_HALF_PX));
  const limit = LANE_HALF_MAX;
  const center = Math.min(limit, Math.max(-limit, Number.isFinite(v.center) ? v.center : 0));
  return { center, half };
}

/** Fits the X view to a span of RPE X (with margins), never narrower than the field's tenth. */
export function fitXView(x0: number, x1: number): { center: number; half: number } {
  const lo = Math.min(x0, x1, -FIELD_HALF_PX);
  const hi = Math.max(x0, x1, FIELD_HALF_PX);
  return clampXView({ center: (lo + hi) / 2, half: ((hi - lo) / 2) * 1.08 });
}

/** Index of the given note in a lane index (re-export helper for overlays). */
export function laneIndexOf(index: LaneNoteIndex, id: NoteId): number {
  return index.indexOf(id);
}

export { EMPTY_NOTE_INDEX };
